import React from 'react';
import {
  Card,
  CardContent,
  Typography,
  Box,
  Chip,
  Grid,
  LinearProgress,
  Alert,
} from '@mui/material';
import { Campaign } from '../../store/api/outboundCallingApi';

interface CampaignStatusProps {
  campaign: Campaign;
}

const CampaignStatus: React.FC<CampaignStatusProps> = ({ campaign }) => {
  const getStatusColor = (status: string) => {
    switch (status) {
      case 'active':
        return 'success';
      case 'paused':
        return 'warning';
      case 'stopped':
        return 'error';
      case 'completed':
        return 'info';
      default:
        return 'default';
    }
  };

  const getProgressPercentage = () => {
    if (campaign.total_contacts === 0) return 0;
    return Math.round((campaign.completed_contacts / campaign.total_contacts) * 100);
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  return (
    <Box>
      {/* Campaign Overview */}
      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
            <Typography variant="h5">{campaign.name}</Typography>
            <Chip
              label={campaign.status.toUpperCase()}
              color={getStatusColor(campaign.status) as any}
              size="medium"
            />
          </Box>

          <Grid container spacing={3}>
            <Grid item xs={12} md={6}>
              <Typography variant="h6" gutterBottom>
                Campaign Details
              </Typography>
              <Typography variant="body2" color="text.secondary">
                <strong>Tag:</strong> {campaign.tag_name}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                <strong>Interval:</strong> {campaign.interval_minutes} minutes
              </Typography>
              <Typography variant="body2" color="text.secondary">
                <strong>Max Duration:</strong> {campaign.max_call_duration_minutes} minutes
              </Typography>
              <Typography variant="body2" color="text.secondary">
                <strong>Max Retries:</strong> {campaign.max_retries}
              </Typography>
            </Grid>

            <Grid item xs={12} md={6}>
              <Typography variant="h6" gutterBottom>
                Schedule
              </Typography>
              <Typography variant="body2" color="text.secondary">
                <strong>Timezone:</strong> {campaign.timezone}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                <strong>Hours:</strong> {campaign.start_hour}:00 - {campaign.end_hour}:00
              </Typography>
              <Typography variant="body2" color="text.secondary">
                <strong>Days:</strong> {campaign.days_of_week.map(day => 
                  day.charAt(0).toUpperCase() + day.slice(1)
                ).join(', ')}
              </Typography>
            </Grid>
          </Grid>

          {/* Progress Section */}
          <Box sx={{ mt: 3 }}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
              <Typography variant="h6">Progress</Typography>
              <Typography variant="body2" color="text.secondary">
                {getProgressPercentage()}%
              </Typography>
            </Box>
            <LinearProgress
              variant="determinate"
              value={getProgressPercentage()}
              sx={{ height: 10, borderRadius: 5, mb: 2 }}
            />
            <Grid container spacing={2}>
              <Grid item xs={4}>
                <Box sx={{ textAlign: 'center' }}>
                  <Typography variant="h4" color="primary">
                    {campaign.total_contacts}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    Total Contacts
                  </Typography>
                </Box>
              </Grid>
              <Grid item xs={4}>
                <Box sx={{ textAlign: 'center' }}>
                  <Typography variant="h4" color="success.main">
                    {campaign.completed_contacts}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    Completed
                  </Typography>
                </Box>
              </Grid>
              <Grid item xs={4}>
                <Box sx={{ textAlign: 'center' }}>
                  <Typography variant="h4" color="error.main">
                    {campaign.failed_contacts}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    Failed
                  </Typography>
                </Box>
              </Grid>
            </Grid>
          </Box>

          {/* Timestamps */}
          <Box sx={{ mt: 3 }}>
            <Typography variant="h6" gutterBottom>
              Timeline
            </Typography>
            <Grid container spacing={2}>
              <Grid item xs={12} sm={6}>
                <Typography variant="body2" color="text.secondary">
                  <strong>Created:</strong> {formatDate(campaign.created_at)}
                </Typography>
              </Grid>
              {campaign.started_at && (
                <Grid item xs={12} sm={6}>
                  <Typography variant="body2" color="text.secondary">
                    <strong>Started:</strong> {formatDate(campaign.started_at)}
                  </Typography>
                </Grid>
              )}
              {campaign.completed_at && (
                <Grid item xs={12} sm={6}>
                  <Typography variant="body2" color="text.secondary">
                    <strong>Completed:</strong> {formatDate(campaign.completed_at)}
                  </Typography>
                </Grid>
              )}
              {campaign.paused_at && (
                <Grid item xs={12} sm={6}>
                  <Typography variant="body2" color="text.secondary">
                    <strong>Paused:</strong> {formatDate(campaign.paused_at)}
                  </Typography>
                </Grid>
              )}
            </Grid>
          </Box>
        </CardContent>
      </Card>

      {/* Current Contact (if active) */}
      {campaign.status === 'active' && (
        <Card sx={{ mb: 3 }}>
          <CardContent>
            <Typography variant="h6" gutterBottom>
              Current Status
            </Typography>
            <Alert severity="info">
              Campaign is currently active and processing contacts.
              Check back for real-time updates.
            </Alert>
          </CardContent>
        </Card>
      )}

    </Box>
  );
};

export default CampaignStatus;
